export const SALES_ORDER_TYPES = {
  API: 'API',
  MANUAL: 'MANUAL'
} as const;

export type SalesOrderType = typeof SALES_ORDER_TYPES[keyof typeof SALES_ORDER_TYPES];

export const SALES_ORDER_LABELS = {
  HEADING: 'Sales order',
  SALES_ORDER_NUMBER_LABEL: 'Sales order #',
  ORDER_TYPE_LABEL: 'Order type',
  REJECTION_STATUS_LABEL: 'Rejection status',
  DELIVERY_STATUS_LABEL: 'Delivery status',
  EDIT_BUTTON: 'Edit',
  ENTER_BUTTON: 'Enter',
  SHOW_ALL_BUTTON: 'Show all',
  API_SALES_ORDER_TEXT: 'API sales order',
  MANUAL_SALES_ORDER_TEXT: 'Manual sales order',
  API_TOOLTIP: 'API is enabled for this sales order. Order will be passed to ERP automatically.',
  MANUAL_TOOLTIP: 'Sales order API is not enabled, this order will be passed to ERP manually.',
  NOT_AVAILABLE: 'NOT AVAILABLE',
  SO_NOT_EXIST: 'Such SO does not exist',
  INTEGRATION_NO_RESPONSE: 'Sales order integration does not respond',
  SALES_ORDER_IN_PROGRESS: 'The sales order is currently being processed. Processing may take several minutes. Please refresh the page to view the latest status.',
  PROCESSING: 'Processing...'
} as const;

export const SALES_ORDER_DETAIL_LABELS = {
  // Modal
  MODAL_TITLE: 'Sales Order Details',
  CLOSE_BUTTON: 'Close',
  
  // Section Headers
  ORDER_SUMMARY: 'Order Summary',
  CUSTOMER_INFORMATION: 'Customer Information',
  FINANCIAL_DETAILS: 'Financial Details',
  DELIVERY_INFORMATION: 'Delivery Information',
  MATERIAL_DETAILS: 'Material Details',
  PROJECT_INFORMATION: 'Project Information',
  BUSINESS_TERMS: 'Business Terms',
  ADMINISTRATIVE_DETAILS: 'Administrative Details',
  TECHNICAL_DETAILS: 'Technical Details',
  
  // Field Labels
  salesOrder: 'Sales Order #',
  item: 'Item',
  salesOrg: 'Sales Organization',
  distributionChannel: 'Distribution Channel',
  division: 'Division',
  docType: 'Document Type',
  soldTo: 'Sold To',
  shipTo: 'Ship To',
  billTo: 'Bill To',
  priceBookName: 'Price Book',
  deliveryBlock: 'Delivery Block',
  billingBlock: 'Billing Block',
  createdOn: 'Created Date',
  createdTime: 'Created Time',
  createdBy: 'Created By',
  poType: 'PO Type',
  docCurrency: 'Currency',
  incoTerms1: 'Inco Terms 1',
  incoTerms2: 'Inco Terms 2',
  paymentTerms: 'Payment Terms',
  contractStartDate: 'Contract Start Date',
  contractEndDate: 'Contract End Date',
  sourceId: 'Source ID',
  integrationId: 'Integration ID',
  sourceValue: 'Source Value',
  quoteNumber: 'Quote Number',
  jobStatus: 'Job Status',
  jobStartDate: 'Job Start Date',
  jobEndDate: 'Job End Date',
  operationalApprover: 'Operational Approver',
  project: 'Project',
  pamProject: 'PAM Project',
  basin: 'Basin',
  geoMarket: 'Geo Market',
  subGeo: 'Sub Geo',
  segment: 'Segment',
  subSeg: 'Sub Segment',
  customerContract: 'Customer Contract',
  requestDeliveryDate: 'Requested Delivery Date',
  performingOrg: 'Performing Organization',
  managementCountry: 'Management Country',
  acceptanceDate: 'Acceptance Date',
  wellName: 'Well Name',
  rigName: 'Rig Name',
  materialNumber: 'Material Number',
  higherLevelItem: 'Higher Level Item',
  plant: 'Plant',
  jobClass: 'Job Class',
  reason4Rejection: 'Reason for Rejection',
  wbsElement: 'WBS Element',
  localMaterialNo: 'Local Material Number',
  localMaterialDesc: 'Local Material Description',
  localUoM: 'Local Unit of Measure',
  salesUnit: 'Sales Unit',
  revenueType: 'Revenue Type',
  profitCenter: 'Profit Center',
  scheduledDeliveryDate: 'Scheduled Delivery Date',
  schduledDeliveryDate: 'Scheduled Delivery Date',
  materialDescription: 'Material Description',
  customer_purchase_no: 'Customer Purchase Number',
  SOItemGUID: 'SO Item GUID',
  deliveryStatus: 'Delivery Status',
  orderRejectionStatus: 'Order Rejection Status',
  netValue: 'Net Value',
  orderQuantity: 'Order Quantity',
  scheduledDeliveryConfirmedQty: 'Scheduled Delivery Confirmed Quantity',
  tax: 'Tax',
  

} as const;

export const SALES_ORDER_FIELD_GROUPS = {
  ORDER_SUMMARY: ['salesOrder', 'item', 'docType', 'jobStatus', 'createdOn', 'createdTime', 'createdBy'],
  CUSTOMER_INFORMATION: ['soldTo', 'shipTo', 'billTo', 'customer_purchase_no', 'customerContract'],
  FINANCIAL_DETAILS: ['netValue', 'tax', 'docCurrency', 'paymentTerms', 'priceBookName', 'profitCenter', 'revenueType'],
  DELIVERY_INFORMATION: ['deliveryStatus', 'orderRejectionStatus', 'requestDeliveryDate', 'schduledDeliveryDate', 'orderQuantity', 'scheduledDeliveryConfirmedQty', 'deliveryBlock', 'billingBlock'],
  MATERIAL_DETAILS: ['materialNumber', 'materialDescription', 'localMaterialNo', 'localMaterialDesc', 'plant', 'localUoM', 'salesUnit'],
  PROJECT_INFORMATION: ['project', 'pamProject', 'basin', 'geoMarket', 'subGeo', 'segment', 'subSeg', 'wellName', 'rigName', 'jobClass'],
  BUSINESS_TERMS: ['incoTerms1', 'incoTerms2', 'contractStartDate', 'contractEndDate', 'acceptanceDate', 'jobStartDate', 'jobEndDate', 'paymentTerms'],
  ADMINISTRATIVE_DETAILS: ['operationalApprover', 'performingOrg', 'managementCountry', 'salesOrg', 'distributionChannel', 'division', 'poType'],
  TECHNICAL_DETAILS: ['sourceId', 'integrationId', 'SOItemGUID', 'wbsElement', 'reason4Rejection', 'higherLevelItem', 'quoteNumber']
} as const;

export const SALES_ORDER_CONFIG_LABELS = {
  SUCCESS_MESSAGE: 'Sales order settings for {BusinessUnit} were updated. They will be used for all future order.',
  ERROR_REQUIRED_MESSAGE: 'Please fill in all fields before saving.',
  SELECT_BUSINESS_UNIT_PROMPT: 'Please choose the business unit to proceed further.',
  GO_TO_BUSINESS_UNIT: 'Go to Business Unit',
  FIELDS_REQUIRED_PROMPT: 'Please note: all fields below are required for the Sales Order to work.',
  OPERATIONAL_APPROVER_LABEL: 'Operational Approver',
  DISTRIBUTION_CHANNEL_LABEL: 'Distribution Channel / Business line',
  DIVISION_LABEL: 'Division',
  MAX_100_CHARS_ERROR: 'Max 100 characters allowed',
  MAX_5_CHARS_ERROR: 'Max 5 characters allowed',
  CANCEL_BUTTON: 'Cancel',
  SAVE_BUTTON: 'Save',
} as const;

export const SALES_ORDER_API_STATUSES = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  SUCCESS: 'SUCCESS',
  RETRY_INITIATED: 'RETRY_INITIATED'
} as const;
